import { find, sortBy, replace } from 'lodash';

import { faAddressCard } from '@fortawesome/free-solid-svg-icons';
import { Component, ViewEncapsulation, Input, OnInit, OnChanges, Output, EventEmitter, ViewChild } from '@angular/core';
import SelfPayType from 'src/app/models/selfPayType';
import SpecialOpenEnrollmentType from 'src/app/models/specialOpenEnrollmentType';
import { env } from 'src/env/development';
import SelfPay from 'src/app/models/selfPay';
import * as dayjs from 'dayjs';
import Subscriber from 'src/app/models/subscriber';

@Component({
  selector: 'self-pay-info',
  templateUrl: 'selfPay.info.component.html',
  styleUrls: [],
  providers: [],
  encapsulation: ViewEncapsulation.None
})
export class SelfPayInfoComponent implements OnInit, OnChanges {
  icons = {
    faAddressCard
  };
  @Output() continue: EventEmitter<void> = new EventEmitter<void>();
  @Output() previous: EventEmitter<void> = new EventEmitter();
  @Output() selectedSpecialOpenEnrollmentType: EventEmitter<SpecialOpenEnrollmentType> = new EventEmitter();
  @Input() requestType: SelfPayType;
  @Input() selectedSpecialOpenEnrollmentTypeId: string;
  @Input() agency: string;
  @Input() selfPay: SelfPay;
  @Input() specialOpenEnrollmentTypes: SpecialOpenEnrollmentType[];
  @Input() subscriber: Subscriber;
  @Input() isSelfPay: boolean = false;
  @Input() isCobra: boolean = false;
  @Input() isSPRetiree: boolean = false;
  @Input() isLwop: boolean = false;
  selfPayTypesList: SpecialOpenEnrollmentType[] = [];
  oeSelfPayTypes: string[] = ['OEC', 'OER', 'OEU'];
  soeSelfPayTypes: string[] = ['SOEC', 'SOER', 'SOEU'];
  selectedSoeType: SpecialOpenEnrollmentType;
  showCustomSOEDescription = false;
  showCustomSOEDocTypes = false;
  soeSPTypesWithCustomDocs: string[] = ['GLEMO', 'MDP', 'MPNLA', 'NEED'];
  coverageYear: number;

  ngOnInit() {
    if (this.selectedSpecialOpenEnrollmentTypeId) 
      this.selectedSoeType = find(this.specialOpenEnrollmentTypes, soet => soet.specialOpenEnrollmentTypeId === this.selectedSpecialOpenEnrollmentTypeId);

    this.coverageYear = dayjs(this.selfPay.coverageEffectiveDate ?? new Date()).year();
  }

   ngOnChanges() {
    if (this.selfPay) {
      this.selfPayTypesList = this.getSelfPayTypes();
    }
   }

  pushContinue() {
    this.continue.emit();
  }

  pushCancel() {
    this.previous.emit();
  }

  selectSoeType(selectedSoeType: SpecialOpenEnrollmentType) {
    this.showCustomSOEDescription = env.soeTypesWithCustomDescriptions.includes(selectedSoeType.specialOpenEnrollmentTypeCode);
    this.showCustomSOEDocTypes = env.soeTypesWithCustomDocTypeInstructions.includes(selectedSoeType.specialOpenEnrollmentTypeCode);

    if (this.selectedSoeType === selectedSoeType) {
      selectedSoeType = null;
    }

    this.selectedSoeType = selectedSoeType;
    this.selectedSpecialOpenEnrollmentType.emit(selectedSoeType);
  }

  getSelfPayTypes(): SpecialOpenEnrollmentType[] {
    const excludeNames =
    [
      'HCAOEC', //'HCA OE Correction',
      'NE', //'Newly Eligible',
      'SUP', //'Supplemental',
      'CSD', //'Change in school district',
      'APII', //'API Import',
      'GLETRI', //'Gain or lose eligibility for TRICARE',
      'RDP', //'Registering Domestic Partnership'
    ];

    if (this.agency === env.sebbCode) {
      //SEBB, exclude PEBB specific SOE types
      excludeNames.push('GLEMO');
      excludeNames.push('PASMED');
      excludeNames.push('CILIS');

      //change doc types that have PEBB in them to SEBB
      // this.specialOpenEnrollmentTypes.forEach((soet:SpecialOpenEnrollmentType) => {
      //   soet.documentTypes.forEach((dt) => {
      //     //dt.documentTypeName = dt.documentTypeName.replace(/(^|\s+)PEBB(?=\s+|$)/g,"SEBB");
      //   });
      // });
    } else {
      //PEBB, exclude SEBB specific SOE types
      // excludeNames.push('Change in school district');
      excludeNames.push('CSD');
      // excludeNames.push('Gain or lose eligibility for Medicare');
      if (!this.isLwop) {
        excludeNames.push('GLEM');
      }
      // excludeNames.push('Become eligible for state premium assistance subsidy for SEBB health plan coverage from Medicaid or CHIP');
      excludeNames.push('PASMEDS');

      if (this.selfPay.isPEBB && this.selfPay.isLwopSOE) {
        excludeNames.push('GLEMO');
      }
    }

    //sort documents by sort order
    this.specialOpenEnrollmentTypes.forEach(soet => { soet.documentTypes = sortBy(soet.documentTypes,'sortOrder');});

    return sortBy(
      this.specialOpenEnrollmentTypes.filter(soe => excludeNames.indexOf(soe.specialOpenEnrollmentTypeCode) === -1 
        && (soe.selfPayTypes.length == 0 || find(soe.selfPayTypes,spt=> spt.selfPayTypeCode == this.requestType.selfPayTypeCode))),
      'specialOpenEnrollmentTypeName');
  }

  isRetiree(selfPayTypeCode: string): boolean {
    return selfPayTypeCode === 'OER' || selfPayTypeCode === 'R';
  }

  isSoeSelfPay(selfPayTypeCode: string): boolean {
    return this.soeSelfPayTypes.includes(selfPayTypeCode);
  }

  canContinue(requestType: SelfPayType, selectedSoeType: SpecialOpenEnrollmentType): boolean {
    if (this.isSoeSelfPay(requestType.selfPayTypeCode)) {
      return [null, undefined].indexOf(selectedSoeType) === -1;
    }

    return true;
  }

  cancelButtonLabel(selfPayTypeCode: string): string {
    if (this.isSoeSelfPay(selfPayTypeCode)) {
      return 'Cancel';
    }

    return 'Previous';
  }

  continueButtonLabel(selfPayTypeCode: string): string {
      return 'Continue';
  }

  getTitle(selfPayTypeCode: string): string {
    if (!this.oeSelfPayTypes.includes(selfPayTypeCode) && !this.soeSelfPayTypes.includes(selfPayTypeCode)) {
      if (selfPayTypeCode === 'R') {
        return `${this.agency} retiree insurance coverage`;
      } else if (selfPayTypeCode === 'U') {
        return `${this.agency} Continuation Coverage (Unpaid Leave)`;;
      }else {
        return `${this.agency} Continuation Coverage (COBRA)`;
      }
    }

    if (this.soeSelfPayTypes.includes(selfPayTypeCode)) {
      return 'Special Open Enrollment events';
    }

    let program = 'Unpaid Leave';
    let agency = 'PEBB';
    if (selfPayTypeCode === 'OEC') {
      program = 'COBRA';
      agency = this.agency;
    } else if (selfPayTypeCode === 'OER') {
      return 'Retiree open enrollment changes';
    } else if (selfPayTypeCode === 'OEU') {
      agency = this.agency;
    }

    return `${agency} Continuation Coverage (${program}) open enrollment changes`;
  }

  displaySPCustomDocs(soeTypeCode) {
    if (!find(this.soeSPTypesWithCustomDocs,soet=> soet == soeTypeCode)) {
      return false;
    }
    else {
      return true;
    }
  }
}
